/* eslint-disable react-hooks/exhaustive-deps */
import { useNZ } from '@nizza/core';
import { Auth } from 'aws-amplify';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authProtectedRouteList, publicRouteList } from 'routes';
import { useDebouncedCallback } from 'use-debounce';
import logger from '~logger';
import {
  isAuthenticatedAtom,
  isFetchingAtom,
  isLoadingAtom,
  userAuthenticatedAtom,
} from './auth.state';

export const useAuthInit = () => {
  const nizza = useNZ();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const setUserAuthenticated = useSetAtom(userAuthenticatedAtom);
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setIsLoading = useSetAtom(isLoadingAtom);
  const setIsFetching = useSetAtom(isFetchingAtom);
  const hasLoaded = useRef(false);

  const protectedRoutes = useMemo(
    () => authProtectedRouteList.map(x => x.path),
    [],
  );

  const publicRoutes = useMemo(() => {
    return publicRouteList
      .filter(x => x.path !== '/select-account')
      .map(
        route =>
          route.path
            .replace(/:\w+/g, '([^/]+)') // Replace parameters like :eventId
            .replace(/\*/g, '.*'), // Handle wildcard routes
      );
  }, []);

  const isPublicRoute = useMemo(() => {
    return publicRoutes.some(pattern =>
      new RegExp(`^${pattern}$`).test(pathname),
    );
  }, [pathname, publicRoutes]);

  const setLoadingState = (state: boolean) => {
    if (!hasLoaded.current) {
      setIsLoading(state);
    } else {
      setIsFetching(state);
    }
  };

  const fetchUserAndToken = useDebouncedCallback(async () => {
    setLoadingState(true);

    try {
      const [currentUser, session] = await Promise.all([
        Auth.currentUserInfo(),
        Auth.currentSession(),
      ]);

      const accessToken = session.getAccessToken().getJwtToken();
      const account = currentUser.attributes['custom:account'];
      nizza.configure({ account, authToken: accessToken });

      setUserAuthenticated(currentUser);
      setIsAuthenticated(true);
    } catch (error: any) {
      if (String(error).includes('No current user')) {
        logger.error('User not authenticated');
        if (protectedRoutes.includes(pathname)) {
          logger.debug('Protected route, redirecting to login');
          navigate('/login');
        }
      } else {
        logger.error('Failed to fetch user or token:', error);
      }

      setIsAuthenticated(false);
    } finally {
      setLoadingState(false);
      hasLoaded.current = true;
    }
  }, 500);

  useEffect(() => {
    if (isPublicRoute) {
      setIsLoading(false);
      setIsFetching(false);
      return;
    }

    fetchUserAndToken();
  }, [pathname, isPublicRoute]);
};
